/* eslint-disable react/jsx-pascal-case */
import { Themed } from 'theme-ui'
import Palette from '../../elements/palette'
import ImgixImg from '../../elements/imgix-img/connected'
import dayjs from '@uswitch/ev-utils/lib/date'
import { useDataSelector, useMeta } from '@uswitch/ev-utils/lib/context/selectors'
import sw from '@uswitch/ev-utils/lib/switch'
import { Container } from '@uswitch/ev-core/react'

const TrustyleAuthor = ({
  title,
  subtitle,
  authorImage,
  titleUrl,
  variant,
  subtitleUrl,
  className,
}) => {
  const getVariant = element => `elements.author.${variant ? variant + '.' + element : element}`

  return (
    <div
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        variant: getVariant('base'),
      }}
      className={className}
    >
      <If condition={authorImage}>
        <Themed.a
          href={titleUrl}
          sx={{
            flexShrink: 0,
            height: [40, 64],
            width: [40, 64],
            marginRight: 'sm',
            variant: getVariant('imageLink'),
          }}
        >
          <authorImage.type
            {...authorImage.props}
            sx={{
              maxHeight: '100%',
              maxWidth: '100%',
              borderRadius: '50%',
              variant: getVariant('image'),
            }}
          />
        </Themed.a>
      </If>

      <div>
        <Themed.h5
          sx={{
            marginTop: 0,
            fontWeight: 'bold',
            paddingBottom: 0,
            marginBottom: [0, 'xxs'],
            variant: getVariant('title'),
          }}
        >
          <Palette
            as={Themed.a}
            href={titleUrl}
            sx={{
              color: 'inherit',
              textDecoration: 'none',
              variant: getVariant('link'),
            }}
            px={{ color: 'textColor' }}
          >
            {title}
          </Palette>
        </Themed.h5>

        {subtitle && (
          <Themed.p
            sx={{
              fontSize: 'xxs',
              marginY: 0,
              variant: getVariant('subtitle'),
            }}
          >
            <Palette
              as={Themed.a}
              href={subtitleUrl}
              sx={{
                textDecoration: 'none',
                variant: getVariant('link'),
              }}
              px={{ color: 'textColor' }}
            >
              {subtitle}
            </Palette>
          </Themed.p>
        )}
      </div>
    </div>
  )
}

const AuthorWithOptionalEditor = ({ author, editor, publishDate, showWrittenBy = false }) => {
  const authorImage = (
    <ImgixImg
      entity={author?.image || author?.photo}
      width={100}
      height={100}
      imgixParams={{ fit: 'crop', crop: 'faces' }}
    />
  )
  const writtenByTitle = `${showWrittenBy ? 'Written by ' : ''}${author?.name}${
    author?.role ? `, ${author?.role}` : ``
  }`

  const editedBySubtitle =
    editor && editor.name ? `Edited by ${editor.name}${editor.role ? `, ${editor.role}` : ''}` : ''

  const fullSubtitle = publishDate
    ? [editedBySubtitle, dayjs(publishDate).format('LL')].filter(i => i).join(', ')
    : editedBySubtitle

  return (
    <TrustyleAuthor
      authorImage={authorImage}
      title={writtenByTitle}
      subtitle={fullSubtitle}
      titleUrl={author?.url}
      subtitleUrl={editor?.url}
      variant={author?.variant}
    />
  )
}

const BroadbandDealsAuthor = ({ author }) => {
  const authorImage = <ImgixImg entity={author?.image} />

  return (
    <TrustyleAuthor
      authorImage={authorImage}
      title={author?.name}
      subtitle={author?.role}
      titleUrl={author?.url}
      subtitleUrl={author?.url}
      variant={author?.variant}
    />
  )
}

const BankrateAuthor = ({ author, publishedAt, updatedAt }) => {
  const authorImage = <ImgixImg entity={author?.image} />
  const dateTimeFormat = useMeta(['authorConfig', 'dateTimeFormat'], 'ddd DD MMM YYYY hh.mm')
  const singleDate = useMeta(['authorConfig', 'singleDate'])

  const publishedAtFormat = 'ddd DD MMM YYYY'
  const updatedAtFormat = dateTimeFormat
  const hidePublishedAt = Boolean(updatedAt && singleDate)

  return (
    <Container sx={{ variant: 'elements.author.outerContainer' }} className='authorContainer'>
      <If condition={publishedAt && !hidePublishedAt}>
        <Themed.p sx={{ variant: 'elements.author.publishedAt' }}>
          {`Published ${dayjs(publishedAt).format(publishedAtFormat)}`}
        </Themed.p>
      </If>
      <Themed.p sx={{ variant: 'elements.author.updatedAt' }}>
        <If condition={updatedAt}>
          {`Updated on ${dayjs(updatedAt).format(updatedAtFormat)} GMT`}
        </If>
      </Themed.p>
      <TrustyleAuthor
        authorImage={authorImage}
        title={author?.name}
        subtitle={author?.role}
        titleUrl={author?.url}
        subtitleUrl={author?.url}
        variant={author?.variant || 'articleDates'}
      />
    </Container>
  )
}

const SOEAuthor = ({ author, publishedAt, updatedAt }) => {
  const authorImage = <ImgixImg entity={author?.image || author?.photo} />
  const dateTimeFormat = useMeta(['authorConfig', 'dateTimeFormat'], 'ddd DD MMM YYYY hh.mm')
  const singleDate = useMeta(['authorConfig', 'singleDate'])

  const formatDate = date => dayjs(date).format(dateTimeFormat)
  const hidePublishedAt = Boolean(updatedAt && singleDate)

  return (
    <Container sx={{ variant: 'elements.author.outerContainer' }}>
      <Themed.p sx={{ variant: 'elements.author.text' }}>Written by</Themed.p>
      <TrustyleAuthor
        authorImage={authorImage}
        title={author?.name}
        subtitle={author?.role}
        titleUrl={author?.url}
        subtitleUrl={author?.url}
        variant={author?.variant}
      />
      <If condition={updatedAt}>
        <Themed.p sx={{ variant: 'elements.author.text', mb: 0 }}>
          {`Updated on ${formatDate(updatedAt)} GMT`}
        </Themed.p>
      </If>
      <If condition={publishedAt && !hidePublishedAt}>
        <Themed.p sx={{ variant: 'elements.author.text' }}>
          {`Published on ${formatDate(publishedAt)} GMT`}
        </Themed.p>
      </If>
    </Container>
  )
}

const Author = props => {
  const { publishDate, updatedDate } = useDataSelector(['dates'])
  const brandTitle = useMeta(['title'])

  return sw({
    'saveonenergy.com': props => (
      <SOEAuthor {...props} publishedAt={publishDate} updatedAt={updatedDate} />
    ),
    'Broadband Deals': props => (
      <BroadbandDealsAuthor {...props} publishedAt={publishDate} updatedAt={updatedDate} />
    ),
    'Bankrate UK': props => (
      <BankrateAuthor {...props} publishedAt={publishDate} updatedAt={updatedDate} />
    ),
    default: props => (
      <AuthorWithOptionalEditor {...props} publishedAt={publishDate} updatedAt={updatedDate} />
    ),
  })(brandTitle, props)
}

export default Author
